import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useColorTheme } from '../../context/ColorThemeContext';
import { BarChart3, TrendingUp, Calendar, Users, Palette, Target } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, icon: Icon, colorTheme, gradient }) => (
  <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-5 text-white shadow-lg`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-white/80 text-xs font-medium mb-1">{label}</p>
        <p className="text-3xl font-bold">{value}</p>
      </div>
      <Icon className="w-8 h-8 opacity-60" />
    </div>
  </div>
);

const DistributionCard = ({ title, data, colorTheme, barColor }) => {
  const maxVal = Math.max(...data.map(d => d.value || d.campaigns || 0), 1);
  const bar = barColor || (colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-emerald-500');
  return (
    <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-5 shadow-sm" data-testid={`chart-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <h3 className="text-sm font-bold text-slate-800 dark:text-white mb-4">{title}</h3>
      <div className="space-y-2.5">
        {data.map((item, i) => (
          <div key={i} className="flex items-center justify-between gap-3">
            <span className="text-xs text-slate-600 dark:text-slate-300 w-28 truncate">{item.name || item.month}</span>
            <div className="flex-1 bg-slate-100 dark:bg-slate-700/50 rounded-full h-2">
              <div className={`${bar} h-2 rounded-full transition-all duration-500`} style={{ width: `${Math.max(8, ((item.value || item.campaigns || 0) / maxVal) * 100)}%` }} />
            </div>
            <span className="text-xs font-semibold text-slate-800 dark:text-white w-8 text-right">{item.value || item.campaigns}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AnalyticsV2 = ({ sessionToken }) => {
  const { colorTheme } = useColorTheme();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timePeriod, setTimePeriod] = useState('all_time');
  const [customDateRange, setCustomDateRange] = useState({ date_from: '', date_to: '' });

  const gradient = colorTheme === 'purple' ? 'from-purple-600 to-pink-600' : colorTheme === 'blue' ? 'from-blue-600 to-sky-500' : 'from-emerald-600 to-teal-500';
  const gradientBg = colorTheme === 'purple' ? 'from-purple-50/80 via-white to-pink-50/50' : colorTheme === 'blue' ? 'from-blue-50/80 via-white to-sky-50/50' : 'from-emerald-50/80 via-white to-teal-50/50';
  const activeBtn = colorTheme === 'purple' ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/20' : colorTheme === 'blue' ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20';
  const inputClass = `px-3 py-2 text-sm border border-slate-200 dark:border-slate-700/50 rounded-xl focus:ring-2 ${colorTheme === 'purple' ? 'focus:ring-purple-500' : colorTheme === 'blue' ? 'focus:ring-blue-500' : 'focus:ring-emerald-500'} focus:border-transparent dark:bg-slate-800/50 dark:text-white`;

  useEffect(() => { fetchAnalytics(); }, [timePeriod, customDateRange]);

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      params.append('time_period', timePeriod);
      if (timePeriod === 'custom' && customDateRange.date_from && customDateRange.date_to) {
        params.append('date_from', customDateRange.date_from);
        params.append('date_to', customDateRange.date_to);
      }
      const response = await axios.get(`${API}/analytics/overview?${params}`, { headers: { Authorization: `Bearer ${sessionToken}` } });
      setAnalyticsData(response.data);
    } catch (err) {
      setError('Failed to load analytics data');
    } finally { setIsLoading(false); }
  };

  if (isLoading) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${gradientBg} dark:from-slate-900 dark:via-slate-900 dark:to-slate-900 flex items-center justify-center`}>
        <div className={`animate-spin rounded-full h-10 w-10 border-4 border-slate-200 dark:border-slate-700 ${colorTheme === 'purple' ? 'border-t-purple-600' : colorTheme === 'blue' ? 'border-t-blue-600' : 'border-t-emerald-600'}`} />
      </div>
    );
  }
  if (error) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${gradientBg} dark:from-slate-900 dark:via-slate-900 dark:to-slate-900 flex items-center justify-center`}>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50 rounded-2xl px-6 py-4 text-sm text-red-700 dark:text-red-300">{error}</div>
      </div>
    );
  }

  const { campaign_stats = {}, performance_metrics = {} } = analyticsData || {};
  const contentTypeData = Object.entries(performance_metrics.content_types || {}).map(([name, value]) => ({ name, value }));
  const categoryData = Object.entries(performance_metrics.categories || {}).map(([name, value]) => ({ name, value }));
  const genderData = Object.entries(performance_metrics.gender_distribution || {}).map(([name, value]) => ({ name, value }));
  const monthlyTrendData = (performance_metrics.monthly_trends || []).slice(0, 6).reverse();

  return (
    <div className={`min-h-screen bg-gradient-to-br ${gradientBg} dark:from-slate-900 dark:via-slate-900 dark:to-slate-900`} data-testid="analytics-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>Analytics Overview</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Insights into your campaign performance and audience targeting</p>
        </div>

        {/* Time Filter */}
        <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-4 mb-6 shadow-sm flex flex-wrap items-center gap-3" data-testid="time-filter">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Period:</span>
          {['all_time', 'last_6_months', 'custom'].map(p => (
            <button key={p} onClick={() => setTimePeriod(p)} className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${timePeriod === p ? activeBtn : 'bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 hover:bg-slate-200'}`} data-testid={`filter-${p}`}>
              {p === 'all_time' ? 'All Time' : p === 'last_6_months' ? 'Last 6 Months' : 'Custom'}
            </button>
          ))}
          {timePeriod === 'custom' && (
            <div className="flex gap-2 ml-2">
              <input type="date" value={customDateRange.date_from} onChange={e => setCustomDateRange(p => ({ ...p, date_from: e.target.value }))} className={inputClass} />
              <input type="date" value={customDateRange.date_to} onChange={e => setCustomDateRange(p => ({ ...p, date_to: e.target.value }))} className={inputClass} />
            </div>
          )}
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatCard label="Total Campaigns" value={campaign_stats.total_campaigns || 0} icon={BarChart3} colorTheme={colorTheme} gradient={gradient} />
          <StatCard label="This Month" value={campaign_stats.this_month_campaigns || 0} icon={TrendingUp} colorTheme={colorTheme} gradient={colorTheme === 'purple' ? 'from-pink-500 to-rose-600' : colorTheme === 'blue' ? 'from-sky-500 to-cyan-600' : 'from-teal-500 to-cyan-600'} />
          <StatCard label="Avg Per Month" value={campaign_stats.avg_campaigns_per_month || 0} icon={Calendar} colorTheme={colorTheme} gradient={colorTheme === 'purple' ? 'from-violet-500 to-purple-700' : colorTheme === 'blue' ? 'from-indigo-500 to-blue-700' : 'from-emerald-700 to-green-800'} />
        </div>

        {/* Distribution Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          {contentTypeData.length > 0 && <DistributionCard title="Content Types" data={contentTypeData} colorTheme={colorTheme} barColor={colorTheme === 'purple' ? 'bg-purple-500' : colorTheme === 'blue' ? 'bg-blue-500' : 'bg-amber-500'} />}
          {categoryData.length > 0 && <DistributionCard title="Categories" data={categoryData} colorTheme={colorTheme} barColor={colorTheme === 'purple' ? 'bg-pink-500' : colorTheme === 'blue' ? 'bg-sky-500' : 'bg-emerald-500'} />}
          {genderData.length > 0 && <DistributionCard title="Target Gender" data={genderData} colorTheme={colorTheme} />}
          {monthlyTrendData.length > 0 && <DistributionCard title="Monthly Trends" data={monthlyTrendData} colorTheme={colorTheme} barColor={colorTheme === 'purple' ? 'bg-violet-500' : colorTheme === 'blue' ? 'bg-indigo-500' : 'bg-teal-500'} />}
        </div>

        {/* Brand Voice Usage */}
        {performance_metrics.brand_voices && Object.keys(performance_metrics.brand_voices).length > 0 && (
          <div className="bg-white/70 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-5 shadow-sm" data-testid="brand-voice-usage">
            <h3 className="text-sm font-bold text-slate-800 dark:text-white mb-4">Brand Voice Usage</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(performance_metrics.brand_voices).map(([voice, count]) => (
                <div key={voice} className="text-center p-4 bg-slate-50/80 dark:bg-slate-700/30 rounded-xl">
                  <div className={`text-2xl font-bold ${colorTheme === 'purple' ? 'text-purple-600' : colorTheme === 'blue' ? 'text-blue-600' : 'text-emerald-600'}`}>{count}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 capitalize mt-0.5">{voice}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsV2;
